/**
 * fire_particles.ts — EMBERS (R3) and SMOKE (R4) for every fire, on shared
 * emitters (fire realism, 2026-09-25).
 *
 * EMBERS. One emitter per session (a "cluster": every fire in range spawns
 * into it at its own origin — one pool, one draw). WebGPU: the engine's
 * GPU-compute emitter with `renderMode: 'stretched'` (a spark reads as a
 * streak along its velocity, never a dot), gravity −0.15 (buoyant: the rise
 * DECAYS through drag 0.8 instead of climbing at a constant 1.1 m/s like the
 * old sprites), curl noise, and a linear-HDR 4-stop ramp (8,6,3) → (4,1.6,0.3)
 * → (1.2,0.25,0.05) → 0 so a young ember blooms and an old one dies red.
 * WebGL 2: the CPU adapter with the same config (no curl, no stretch — the
 * adapter's limits). THE FRAME LAW: the GPU kernels spawn in RENDER space, so
 * a fire's ABSOLUTE point is seated at `+ scene.position`; the CPU adapter's
 * instances are scene children in ABSOLUTE coordinates.
 * Budget: `particleCap × 120` embers per cluster (the tier's particle cap).
 *
 * SMOKE — the single biggest DAYTIME cue of a fire. WebGPU with `litSmoke`
 * (med+): a SIX-WAY lit column (the same baked flipbooks as the impact
 * puffs, lib/lit_smoke.ts), lit by the sun / sky (`sixWayLightFor`) and lit
 * FROM BENEATH through the six-way EMISSIVE channel: the uniform carries the
 * fire's colour and the bake's emission fades over the first third of a
 * puff's life — ≈ the first 2 m of rise — so the column's base glows orange
 * and its top is plain grey (fire colour × (1 − h/2 m)). Elsewhere (WebGL 2,
 * low): the fire system draws 1–2 cylindrical SMOKE CARDS per fire
 * (lib/vfx/fire_volume.ts `fireSmokeCardMaterial`).
 *
 * ON THE CURVE (session 3, 2026-09-26). The ember ramp is authored in the
 * shaders' own units and tinted by the fire's radiance scale every frame
 * (`setEmberScale`: the GPU ramp uniforms, the CPU adapter's material colour).
 * The six-way smoke is NOT in the shared `sixWayHandles` any more (that
 * updater writes the old fixed-unit light): the fire system lights it itself —
 * the sun and the sky in scene units (`setSmokeLight`), the under-glow × the
 * fire's radiance scale (`setSmokeGlow`).
 */
import * as THREE from 'three/webgpu';
import type { App } from 'enginex';
import { GpuParticleEmitter, CpuParticleAdapter } from 'enginex/particles/gpu';
import { EMBER_RAMP, EMBER_LIFE_S, EMBER_GRAVITY, EMBER_DRAG, EMBER_SIZE_M, EMBER_STRETCH, emberBudget } from './fire_rules.js';
import { litSmokeMaps } from '../lit_smoke.js';
import { softDiscTexture } from '../vehicle_fx.js';

type Emitter = GpuParticleEmitter | CpuParticleAdapter;

export interface FireParticles {
  embers: Emitter | null;
  smoke: GpuParticleEmitter | null;
  backend: 'gpu' | 'cpu' | 'none';
  emberCap: number;
  stats: { embers: number; smokePuffs: number; bursts: number };
  /** The GPU ember ramp's four stops as authored (the tint multiplies these), null on the CPU adapter. */
  emberRamp: THREE.Vector4[] | null;
  /** The last tint written (skips the uniform writes while it holds). */
  emberScale: number;
}

type RampUniforms = { color0: { value: THREE.Vector4 }; color1: { value: THREE.Vector4 }; color2: { value: THREE.Vector4 }; color3: { value: THREE.Vector4 } };
type SixWayUniforms = { lightDirWorld: { value: THREE.Vector3 }; lightColor: { value: THREE.Vector3 }; ambientColor: { value: THREE.Vector3 }; emissiveColor: { value: THREE.Vector3 } };
const sixWayOf = (fp: FireParticles): SixWayUniforms | undefined => (fp.smoke?.mesh.material as { sixWayUniforms?: SixWayUniforms } | undefined)?.sixWayUniforms;

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _up = new THREE.Vector3(0, 1, 0);
const _dir = new THREE.Vector3();
const _one = new THREE.Vector3(1, 1, 1);
const _scl = new THREE.Vector3(1, 1, 1);
const _p = new THREE.Vector3();
/** The spawn origin (a scratch: `emitEmbers` / `emitSmoke` run per emission, never allocate — session 3). */
const _pos = new THREE.Vector3();

/** Build the shared emitters (null members where the tier or the backend says no). */
export function createFireParticles(app: App, opts: { particleCap: number; litSmoke: boolean }): FireParticles {
  const out: FireParticles = { embers: null, smoke: null, backend: 'none', emberCap: 0, stats: { embers: 0, smokePuffs: 0, bursts: 0 }, emberRamp: null, emberScale: 1 };
  const gpu = !!(app.renderer as { isWebGPU?: boolean }).isWebGPU;
  const cap = emberBudget(opts.particleCap) * 4; // four fires' worth in one cluster
  if (cap >= 16) {
    const ramp = EMBER_RAMP.map(([r, g, b], i) => ({ time: i / (EMBER_RAMP.length - 1), color: new THREE.Color(r, g, b) }));
    const cfg = {
      maxParticles: cap,
      lifetime: { min: EMBER_LIFE_S[0], max: EMBER_LIFE_S[1] },
      startSpeed: { min: 0.7, max: 2.2 },
      startSize: { min: EMBER_SIZE_M[0], max: EMBER_SIZE_M[1] }, // a spark is a centimetre (fire_rules EMBER_SIZE_M: measured)
      startColor: new THREE.Color(8, 6, 3),
      simulationSpace: 'world' as const,
      shape: { type: 'cone' as const, angle: 0.55, radius: 0.22, height: 0.1 },
      emission: { rateOverTime: 0 },
      colorOverLifetime: ramp,
      alphaOverLifetime: [{ time: 0, value: 1 }, { time: 0.7, value: 0.9 }, { time: 1, value: 0 }],
      sizeOverLifetime: [{ time: 0, value: 1 }, { time: 1, value: 0.5 }],
      forces: { gravityModifier: EMBER_GRAVITY, drag: EMBER_DRAG, curl: { strength: 0.6, frequency: 1.2, scrollSpeed: 0.4 } },
      gravityModifier: EMBER_GRAVITY,
      noise: { strength: 0.35, frequency: 1.2, scrollSpeed: 0.4 },
      renderMode: 'stretched' as const,
      velocityStretch: EMBER_STRETCH,
      blending: THREE.AdditiveBlending,
      texture: gpu ? undefined : softDiscTexture(),
    };
    const e = gpu ? new GpuParticleEmitter(app.renderer as never, cfg as never) : new CpuParticleAdapter(cfg as never);
    (e.mesh.material as THREE.Material).premultipliedAlpha = true;
    e.mesh.renderOrder = 14;
    e.mesh.name = 'fire-embers';
    e.mesh.frustumCulled = false;
    app.world.scene.add(e.mesh);
    e.play();
    out.embers = e;
    out.backend = gpu ? 'gpu' : 'cpu';
    out.emberCap = cap;
    const ru = (e.mesh.material as { rampUniforms?: RampUniforms }).rampUniforms;
    if (gpu && ru) out.emberRamp = [ru.color0.value.clone(), ru.color1.value.clone(), ru.color2.value.clone(), ru.color3.value.clone()];
  }
  const maps = gpu && opts.litSmoke ? litSmokeMaps() : null;
  if (maps) {
    const s = new GpuParticleEmitter(app.renderer as never, {
      // (fire realism, 2026-10-08) The column was 2.6 dark balls a second, 0.7–1 m across at birth and 60 % opaque: a
      // clump of blue-black puffs hanging over the fire. Measured on daylight photographs wood smoke is PALE (a thin
      // grey-white veil, brighter than a clear sky's blue, never black) and continuous: more, smaller, thinner puffs
      // that overlap into one wavering column, born the width of the flame's tips.
      maxParticles: 384,
      lifetime: { min: 6, max: 10 },
      startSpeed: { min: 0.9, max: 1.6 },
      startSize: { min: 0.32, max: 0.5 },
      startColor: new THREE.Color(0xcfcac4), // wood smoke's albedo is high: sunlit it is brighter than the sky behind it
      simulationSpace: 'world',
      shape: { type: 'cone', angle: 0.16, radius: 0.14, height: 0.1 },
      emission: { rateOverTime: 0 },
      sizeOverLifetime: [{ time: 0, value: 0.9 }, { time: 0.3, value: 2.4 }, { time: 1, value: 5.2 }],
      alphaOverLifetime: [{ time: 0, value: 0 }, { time: 0.08, value: 0.22 }, { time: 0.5, value: 0.13 }, { time: 1, value: 0 }], // thinner puffs, more of them (SMOKE_RATE): a column, not a string of balls
      forces: { gravityModifier: -0.02, drag: 0.1, curl: { strength: 0.42, frequency: 0.3, scrollSpeed: 0.2 } },
      renderMode: 'billboard',
      blending: THREE.NormalBlending,
      sixWay: { mapA: maps.a, mapB: maps.b, columns: 8, rows: 8 },
    } as never);
    (s.mesh.material as THREE.Material).premultipliedAlpha = true;
    s.mesh.renderOrder = 11; // under the embers and the flame, over the world
    s.mesh.name = 'fire-smoke';
    s.mesh.frustumCulled = false;
    app.world.scene.add(s.mesh);
    // NOT registered in stage5's shared `sixWayHandles` (its updater writes the old fixed-unit light): the fire system
    // lights this column itself each frame (`setSmokeLight` / `setSmokeGlow`, session 3 — on the curve).
    const uni = (s.mesh.material as { sixWayUniforms?: SixWayUniforms }).sixWayUniforms;
    if (uni) uni.emissiveColor.value.set(1.6, 0.62, 0.18);
    s.play();
    out.smoke = s;
  }
  return out;
}

/**
 * Spawn `n` embers at a fire's ABSOLUTE base (x, y, z), the cone leaning
 * downwind (the kernel ignores rotation for the drift, so the lean is baked
 * into the spawn basis — the emitter's +Y tips toward the wind).
 */
export function emitEmbers(fp: FireParticles, scene: THREE.Scene, x: number, y: number, z: number, n: number, windX: number, windZ: number, lean: number): void {
  const e = fp.embers;
  if (!e || n <= 0) return;
  const sp = fp.backend === 'gpu' ? scene.position : _p.set(0, 0, 0); // THE FRAME LAW
  _dir.set(windX * lean, 1, windZ * lean).normalize();
  _q.setFromUnitVectors(_up, _dir);
  _m.compose(_pos.set(x + sp.x, y + sp.y, z + sp.z), _q, _one);
  e.worldMatrix.copy(_m);
  if (fp.backend === 'gpu') (e as GpuParticleEmitter).syncSpawn();
  e.emit(Math.min(n, 24));
  fp.stats.embers += n;
}

/** Spawn `n` smoke puffs at a fire's ABSOLUTE plume root. */
export function emitSmoke(fp: FireParticles, scene: THREE.Scene, x: number, y: number, z: number, n: number, windX: number, windZ: number, lean: number, spread = 1): void {
  const s = fp.smoke;
  if (!s || n <= 0) return;
  const sp = scene.position; // GPU only: RENDER space
  _dir.set(windX * lean, 1, windZ * lean).normalize();
  _q.setFromUnitVectors(_up, _dir);
  // `spread`: the spawn disc is the width of THIS fire's flame tips (the kernel spawns through the matrix's 3×3)
  _m.compose(_pos.set(x + sp.x, y + sp.y, z + sp.z), _q, _scl.set(spread, 1, spread));
  s.worldMatrix.copy(_m);
  s.syncSpawn();
  s.emit(Math.min(n, 12));
  fp.stats.smokePuffs += n;
}

/** Advance the shared emitters (the CPU adapter also billboards against the camera). */
export function updateFireParticles(fp: FireParticles, dt: number, camera: THREE.Camera | null): void {
  if (fp.embers) {
    fp.embers.update(dt);
    if (fp.backend === 'cpu' && camera) (fp.embers as CpuParticleAdapter).render(camera);
  }
  fp.smoke?.update(dt);
}

/** The smoke's under-glow: the fire colour × the fires' combined brightness (0 when none burn). */
export function setSmokeGlow(fp: FireParticles, r: number, g: number, b: number): void {
  sixWayOf(fp)?.emissiveColor.value.set(r, g, b);
}

/**
 * The six-way column's light: the direction TOWARD the light, its colour (the albedo's multiplier — E / π for a Lambert
 * reflector) and the ambient's. The fire system writes it every frame (the sun + the sky in scene units on the curve).
 */
export function setSmokeLight(fp: FireParticles, dir: readonly [number, number, number], color: readonly [number, number, number], ambient: readonly [number, number, number]): void {
  const u = sixWayOf(fp);
  if (!u) return;
  u.lightDirWorld.value.set(dir[0], dir[1], dir[2]);
  u.lightColor.value.set(color[0], color[1], color[2]);
  u.ambientColor.value.set(ambient[0], ambient[1], ambient[2]);
}

/** Tint the embers by the fire's radiance scale (the GPU ramp as authored × k; the CPU adapter's material colour). */
export function setEmberScale(fp: FireParticles, k: number): void {
  if (!fp.embers || Math.abs(k - fp.emberScale) <= 1e-4 * Math.max(1e-6, fp.emberScale)) return;
  fp.emberScale = k;
  if (fp.emberRamp) {
    const ru = (fp.embers.mesh.material as { rampUniforms?: RampUniforms }).rampUniforms;
    if (ru) {
      const stops = [ru.color0, ru.color1, ru.color2, ru.color3];
      for (let i = 0; i < 4; i++) { const b = fp.emberRamp[i]; stops[i].value.set(b.x * k, b.y * k, b.z * k, b.w); }
    }
  } else (fp.embers.mesh.material as THREE.MeshBasicMaterial).color?.setScalar(k);
}
